import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Alert 
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import Toast from '../common/Toast';

const AddPatient = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: 'Male',
    bloodGroup: 'None',
    contactNumber: '',
    address: '',
    history: '',
    referredBy: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  const { name, age, gender, bloodGroup, contactNumber, address, history, referredBy } = formData;
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate form
      if (!name || !age || !gender || !contactNumber) {
        setError('Please fill all required fields');
        setLoading(false);
        return;
      }
      
      // Validate age is a number
      if (isNaN(age) || parseInt(age) <= 0) {
        setError('Age must be a valid positive number');
        setLoading(false);
        return;
      }
      
      // Convert age to a number
      const patientData = {
        ...formData,
        age: parseInt(age)
      };
      
      const res = await axios.post('/api/patients.php', patientData);
      
      setLoading(false);
      setToastMessage('Patient added successfully!');
      setShowToast(true);
      
      setTimeout(() => {
        // Navigate to patients list instead of patient detail
        navigate('/patients');
      }, 1500);
    } catch (err) {
      console.error('Error creating patient:', err);
      setError(err.response?.data?.msg || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };
  
  return (
    <div>
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage} 
        type="success" 
      />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add New Patient</h2>
        <Link to="/patients">
          <Button variant="outline-secondary">
            <FaTimes className="me-2" /> Cancel
          </Button>
        </Link>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Patient Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={onChange}
                    placeholder="Enter patient name"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Age *</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    value={age}
                    onChange={onChange}
                    placeholder="Enter age"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender *</Form.Label>
                  <Form.Select
                    name="gender"
                    value={gender}
                    onChange={onChange}
                    required
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Blood Group</Form.Label>
                  <Form.Select
                    name="bloodGroup"
                    value={bloodGroup}
                    onChange={onChange}
                  >
                    <option value="None">None</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactNumber"
                    value={contactNumber}
                    onChange={onChange}
                    placeholder="Enter contact number"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={address}
                    onChange={onChange}
                    placeholder="Enter address"
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Referred By</Form.Label>
                  <Form.Control
                    type="text"
                    name="referredBy"
                    value={referredBy}
                    onChange={onChange}
                    placeholder="Doctor/Person name"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Medical History</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="history"
                value={history}
                onChange={onChange}
                placeholder="Enter any relevant medical history"
              />
            </Form.Group>
            
            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  'Saving...'
                ) : (
                  <>
                    <FaSave className="me-2" /> Save Patient
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddPatient; 
